import { useEffect, useRef, useState } from "react";
import { Search, X, Loader2, ArrowUpRight } from "lucide-react";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";

export const GlobalSearch = ({ onChoose }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const { items, loading, error } = useGlobalSearch(query);
  const ready = query.trim().length > 0;

  useEffect(() => { setActiveIndex(-1); }, [items]);
  useEffect(() => {
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, []);

  const choose = (item) => {
    setOpen(false);
    onChoose(item);
  };
  const clear = () => {
    setQuery("");
    setOpen(false);
  };
  const onKeyDown = (event) => {
    if (!open || items.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? items.length - 1 : index - 1));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      choose(items[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative px-3 pt-3 z-30" data-testid="global-search-container">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-sky-700/70 pointer-events-none" />
        <input value={query} onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onKeyDown={onKeyDown} placeholder="Search all records…"
          aria-label="Search all report records" aria-expanded={open && ready}
          data-testid="global-search-input"
          className="h-9 w-full rounded-lg border border-sky-300/70 bg-white/65 pl-9 pr-8 text-xs font-semibold text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] outline-none transition-[border-color,background-color,box-shadow] duration-200 placeholder:font-medium placeholder:text-slate-400 hover:bg-white/80 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/15" />
        {query && (
          <button type="button" onClick={clear} aria-label="Clear search" data-testid="global-search-clear"
            className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-slate-400 transition-colors hover:bg-sky-100 hover:text-slate-700">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && ready && (
        <div className="absolute left-3 right-3 top-[3.25rem] max-h-[46vh] overflow-y-auto rounded-lg border border-sky-200 bg-white/95 p-1.5 shadow-[0_18px_45px_rgba(15,23,42,0.22)] backdrop-blur-xl"
          data-testid="global-search-results" role="listbox">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-4 text-xs font-medium text-slate-500" data-testid="global-search-loading">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />Searching…
            </div>
          )}
          {!loading && error && (
            <p className="px-3 py-4 text-xs font-semibold text-rose-600" data-testid="global-search-error">Search is temporarily unavailable.</p>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="px-3 py-4 text-xs font-medium text-slate-500" data-testid="global-search-empty">No matching records.</p>
          )}
          {!loading && !error && items.map((item, index) => (
            <button key={`${item.report_id}-${item.record_id}`} type="button" role="option"
              aria-selected={activeIndex === index} onMouseEnter={() => setActiveIndex(index)} onClick={() => choose(item)}
              data-testid={`global-search-result-${item.record_id}`}
              className={`group w-full border-b border-slate-100 px-2.5 py-2.5 text-left last:border-b-0 transition-[background-color,transform] duration-150 hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${activeIndex === index ? "bg-sky-50 translate-x-0.5" : ""}`}>
              <span className="flex items-start justify-between gap-2">
                <span className="min-w-0">
                  <span className="block truncate text-[10px] font-extrabold uppercase text-blue-700" data-testid={`global-search-result-report-${item.record_id}`}>{item.report_name}</span>
                  <span className="mt-0.5 block truncate text-xs font-bold text-slate-900" data-testid={`global-search-result-title-${item.record_id}`}>{item.title}</span>
                  <span className="mt-0.5 block line-clamp-2 text-[11px] leading-4 text-slate-500" data-testid={`global-search-result-subtitle-${item.record_id}`}>{item.subtitle}</span>
                </span>
                <ArrowUpRight className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-300 transition-[color,transform] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-blue-600" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};